import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle, Ticket, Ban, RefreshCw, Scan, Gift, CalendarPlus, Send, Edit3, UserPlus, UserMinus, Archive, ArchiveRestore } from 'lucide-react';

/**
 * Maps an activity `kind` → icon + gold/red/green accent.
 * Kept centralized so list + detail views stay visually consistent.
 */
const KIND_META = {
  ticket_purchased:   { Icon: Ticket,         color: '#C6A66B', label: 'Purchased' },
  access_granted:     { Icon: CheckCircle,    color: '#6EE7B7', label: 'Granted'   },
  pass_revoked:       { Icon: Ban,            color: '#C44545', label: 'Revoked'   },
  replacement_issued: { Icon: RefreshCw,      color: '#D4AF37', label: 'Replaced'  },
  pass_used:          { Icon: Scan,           color: '#C6A66B', label: 'Checked in'},
  comp_issued:        { Icon: Gift,           color: '#D4AF37', label: 'Comp'      },
  event_created:      { Icon: CalendarPlus,   color: '#AFAFAF', label: 'New event' },
  message_sent:       { Icon: Send,           color: '#AFAFAF', label: 'Message'   },
  event_edited:       { Icon: Edit3,          color: '#C6A66B', label: 'Edited'    },
  staff_assigned:     { Icon: UserPlus,       color: '#6EE7B7', label: 'Staff +'   },
  staff_removed:      { Icon: UserMinus,      color: '#AFAFAF', label: 'Staff −'   },
  event_archived:     { Icon: Archive,        color: '#777777', label: 'Archived'  },
  event_unarchived:   { Icon: ArchiveRestore, color: '#C6A66B', label: 'Unarchived'},
};

function safeRelative(iso) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return '';
  }
}

/**
 * ActivityFeedItem — single row. Purely presentational.
 * Props: item, showEventTitle (default true), onNavigate(link)
 */
export function ActivityFeedItem({ item, showEventTitle = true, onNavigate }) {
  const meta = KIND_META[item.kind] || { Icon: Ticket, color: '#AFAFAF', label: item.kind };
  const Icon = meta.Icon;
  const clickable = !!item.link && !!onNavigate;

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onNavigate(item.link) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onNavigate(item.link); } : undefined}
      className={`flex items-start gap-3 px-4 py-3 border-b border-[#111] last:border-0 ${clickable ? 'hover:bg-[#0D0D0D] cursor-pointer transition-colors' : ''}`}
      data-testid={`activity-item-${item.id}`}
    >
      <div
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5"
        style={{ backgroundColor: `${meta.color}1A`, border: `1px solid ${meta.color}40` }}
      >
        <Icon className="w-4 h-4" style={{ color: meta.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className="text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: meta.color }}
          >
            {meta.label}
          </span>
          {showEventTitle && item.event_title && (
            <span className="text-[10px] text-[#555] truncate">· {item.event_title}</span>
          )}
        </div>
        <div className="text-sm text-[#EAE7E1] mt-0.5 break-words">{item.summary}</div>
        <div className="flex items-center gap-2 mt-1 text-[11px] text-[#777]">
          <span>{safeRelative(item.at)}</span>
          {item.actor_name && <span>· by {item.actor_name}</span>}
        </div>
      </div>
    </div>
  );
}
